import classNames from "classnames";
import React, { useContext, useEffect, useRef, useState } from "react";

import { createStyles, Theme, withStyles, WithStyles } from "@material-ui/core/styles";

import _ from "app/lang";
import { getUserRoleTranslated } from "app/models";
import { pageContext, PageLevel } from "app/page/ContextProvider";
import { appBarHeight, drawerMenuHeaderHeight, drawerWidth } from "app/theme";

import CloudMenu from "./CloudMenu";
import DeviceMenu from "./DeviceMenu";
import DrawerSiteSelector, { siteSelectorHeight } from "./DrawerSiteSelector";
import MenuHeader from "./MenuHeader";
import SimpleMenuItem from "./SimpleMenuItem";
import SiteMenu from "./SiteMenu";

const styles = (theme: Theme) => {
    const transition = theme.transitions.create("left", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    });

    return createStyles({
        root: {
            position: "fixed",
            top: appBarHeight,
            bottom: 0,
            width: drawerWidth,
            overflow: "hidden",
            background: theme.colors.drawer.background,
            boxShadow: "1px 1px 3px 0 rgba(213, 213, 213, 0.5), 0.5px 0.5px 1px 0 rgba(236, 236, 236, 0.5)",
            color: theme.colors.drawer.main,
            [theme.breakpoints.down("sm")]: {
                zIndex: theme.zIndex.drawer,
                top: 0,
                boxShadow: "none",
            }
        },

        open: {
            [theme.breakpoints.down("sm")]: {
                left: 0,
                transition,
            },
        },

        closing: {
            [theme.breakpoints.down("sm")]: {
                left: -drawerWidth,
                transition
            },
        },

        closed: {
            [theme.breakpoints.down("sm")]: {
                left: -drawerWidth
            },
        },

        menuContainer: {
            position: "absolute",
            top: 0,
            width: 3 * drawerWidth,
            bottom: 46,
            overflowY: "auto",
            overflowX: "hidden",
            "-ms-overflow-style": "-ms-autohiding-scrollbar",
            transition,
            borderBottom: theme.colors.drawer.borderBottom
        },

        menuOuter: {
            display: "inline-block",
            position: "relative",
            width: drawerWidth,
            verticalAlign: "top",
            height: "100%"
        },

        menuHeader: {
            fontFamily: theme.typography.fontFamily,
            fontSize: 16,
            textTransform: "uppercase",
            color: "#fff",
            height: drawerMenuHeaderHeight,
            width: drawerWidth,
            position: "absolute",
            top: 0
        },

        menuInner: {
            position: "absolute",
            top: drawerMenuHeaderHeight + siteSelectorHeight,
            bottom: 0,
            width: drawerWidth,
            overflowX: "hidden",
            overflowY: "auto",
            outline: "none",
            "-ms-overflow-style": "-ms-autohiding-scrollbar",
            overscrollBehaviorY: "contain",
            scrollSnapType: "y mandatory",
        },

        headerButton: {
            height: 22,
            fontSize: 18,
            color: "inherit",
            verticalAlign: "middle"
        },

        cloudMenu: {
            "& $menuHeader": {
                backgroundColor: theme.colors.level.cloud.main,
            }
        },

        siteMenu: {
            "& $menuHeader": {
                backgroundColor: theme.colors.level.site.main
            }
        },

        deviceMenu: {
            "& $menuHeader": {
                backgroundColor: theme.colors.level.device.main
            }
        },

        footer: {
            position: "fixed",
            bottom: 0,
            width: "inherit"
        }
    });
};

export type DrawerState = "open" | "closing" | "closed";

interface Props extends WithStyles<typeof styles> {
    state: DrawerState;
    onAnimationEnd(): void;
}

function Drawer(props: Props) {
    const context = useContext(pageContext);
    const { classes } = props;
    const [ activeMenu, setActiveMenu ] = useState<PageLevel>(context.pageLevel);
    const menuContainer = useRef<HTMLDivElement>(null);

    useEffect(() => setActiveMenu(context.pageLevel), [context.pageLevel]);

    function onTransitionEnd(event: React.TransitionEvent) {
        if (event.target === menuContainer.current) {
            updateMenuFocus();
        }
    }

    function renderSiteSelector() {
        let selected = null;

        if (context.site) {
            selected = {
                id: context.site.id,
                name: context.site.name,
            };
        }
        return (
            <DrawerSiteSelector selected={selected} />
        );
    }

    function renderCloudMenu() {
        const { pageLevel } = context;

        return (
            <div className={classNames(classes.menuOuter, classes.cloudMenu)} data-level="cloud">
                <MenuHeader className={classes.menuHeader}
                    forwardTip={pageLevel !== "cloud" ? _("Site Menu") : undefined}
                    onForward={() => setActiveMenu("site")}
                >
                    {_("Cloud Menu")}
                </MenuHeader>
                {renderSiteSelector()}
                <div className={classes.menuInner} tabIndex={-1}>
                    <CloudMenu />
                </div>
            </div>
        );
    }

    function renderSiteMenu() {
        if (!context.site) {
            return null;
        }

        const { pageLevel } = context;

        return (
            <div className={classNames(classes.menuOuter, classes.siteMenu)} data-level="site">
                <MenuHeader className={classes.menuHeader}
                    backTip={_("Cloud Menu")}
                    onBack={() => setActiveMenu("cloud")}
                    forwardTip={pageLevel === "device" ? _("Device Menu") : undefined}
                    onForward={() => setActiveMenu("device")}
                >
                    {_("Site Menu")}
                </MenuHeader>
                {renderSiteSelector()}
                <div className={classes.menuInner} tabIndex={-1}>
                    <SiteMenu />
                </div>
            </div>
        );
    }

    function renderDeviceMenu() {
        if (!context.device || !context.site) {
            return null;
        }

        return (
            <div className={classNames(classes.menuOuter, classes.deviceMenu)} data-level="device">
                <MenuHeader className={classes.menuHeader}
                    backTip={_("Site Menu")}
                    onBack={() => setActiveMenu("site")}
                >
                    {_("Device Menu")}
                </MenuHeader>
                {renderSiteSelector()}
                <div className={classes.menuInner} tabIndex={-1}>
                    <DeviceMenu />
                </div>
            </div>
        );
    }

    function updateMenuFocus() {
        if (menuContainer.current === null) {
            return;
        }

        for (const child of menuContainer.current.children) {
            if (child instanceof HTMLElement) {
                if (child.dataset.level === activeMenu) {
                    const focusHolder = child.querySelector<HTMLElement>(".focus-container");

                    // This neat trick will make the first element in the
                    // newly visible menu header to focus as soon as user
                    // presses the Tab key

                    if (focusHolder) {
                        focusHolder.style.display = "block";
                        focusHolder.focus();
                        focusHolder.style.display = "none";
                    }
                }
            }
        }
    }

    const offsets: Record<PageLevel, number> = {
        "cloud": 0,
        "site": -1 * drawerWidth,
        "device": -2 * drawerWidth
    };

    const rootClasses = [
        classes.root,
        classes[props.state]
    ];

    let menus = null;
    let footer = null;

    if (context.cloud && context.userCloud) {
        const userRole = getUserRoleTranslated(context.userCloud.role_id);

        menus = (
            <>
                {renderCloudMenu()}
                {renderSiteMenu()}
                {renderDeviceMenu()}
            </>
        );

        footer = (
            <SimpleMenuItem
                text={userRole}
                icon="account_circle"
                href={`/v2/cloud/${context.cloud.id}/clouds`}
            />
        );
    }

    return (
        <div className={rootClasses.join(" ")} onAnimationEnd={props.onAnimationEnd}>
            <div className={classNames(classes.menuContainer, "track-menu")}
                ref={menuContainer}
                style={{ left: offsets[activeMenu] }}
                onTransitionEnd={onTransitionEnd}
            >
                {menus}
            </div>
            <div className={classes.footer}>
                {footer}
            </div>
        </div>
    );
}

export default withStyles(styles)(Drawer);